import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ShoppingBag, Share2, Heart, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { inr, resolveImg } from "../lib/api";

export default function SharedLook() {
  const { shareId } = useParams();
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/looks/shared/${shareId}`).then(r => {
      setLook(r.data);
      setLoading(false);
    }).catch(() => {
      setLook(null);
      setLoading(false);
    });
  }, [shareId]);

  const copyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for insecure contexts
      const input = document.createElement("input");
      input.value = window.location.href;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!look) {
    return (
      <div className="max-w-xl mx-auto px-5 py-28 text-center" data-testid="look-not-found">
        <h1 className="text-3xl text-primary font-medium mb-4">Look not found</h1>
        <p className="text-muted-foreground mb-6">This look may have been removed or the link is invalid.</p>
        <Link to="/build-my-look" className="bg-primary text-primary-foreground px-8 py-3 text-sm">
          Create Your Own Look
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="shared-look-page">
      {/* Header */}
      <div className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold">Aethel Decor Look</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display text-primary">{look.name}</h1>
        {look.description && <p className="mt-3 text-muted-foreground max-w-lg mx-auto">{look.description}</p>}
        <div className="flex items-center justify-center gap-4 mt-4 text-sm text-muted-foreground">
          {look.room && <span className="bg-secondary px-3 py-1">{look.room}</span>}
          <span>{look.items.length} item{look.items.length > 1 ? "s" : ""}</span>
          <span className="font-semibold text-primary">{inr(look.total_price)}</span>
          <span>{look.views} view{look.views !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {look.items.map((item, idx) => {
          const p = item.product;
          if (!p) return null;
          return (
            <Link
              key={`${p.id}-${idx}`}
              to={`/product/${p.id}`}
              className="group border border-border hover:border-primary/30 transition-colors"
              data-testid={`shared-look-item-${p.id}`}
            >
              <div className="aspect-[4/5] bg-muted overflow-hidden">
                <img
                  src={resolveImg(p.images?.[0])}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              <div className="p-3 sm:p-4">
                <p className="text-sm line-clamp-2 leading-snug group-hover:text-primary transition-colors">{p.name}</p>
                <p className="text-sm font-semibold text-primary mt-1.5">{inr(p.price)}</p>
                {item.note && <p className="text-xs text-muted-foreground mt-1 italic">"{item.note}"</p>}
                <p className="text-xs text-muted-foreground mt-1">{p.category}</p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
        <button
          onClick={copyLink}
          className="flex items-center gap-2 border border-primary text-primary px-6 py-3 text-sm hover:bg-secondary transition-colors"
          data-testid="look-copy-link"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
          {copied ? "Copied!" : "Copy Share Link"}
        </button>
        <Link
          to="/shop"
          className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 text-sm hover:bg-primary/90 transition-colors"
          data-testid="look-shop-all"
        >
          <ShoppingBag size={16} /> Shop the Collection
        </Link>
        <Link
          to="/build-my-look"
          className="flex items-center gap-2 border border-border text-foreground px-6 py-3 text-sm hover:border-primary/40 transition-colors"
          data-testid="look-create-own"
        >
          <Heart size={16} /> Create Your Own Look
        </Link>
      </div>

      {/* Branding */}
      <div className="text-center mt-12 pt-8 border-t border-border">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Curated with</p>
        <p className="font-display text-xl text-primary mt-1">Aethel Decor</p>
        <p className="text-xs text-muted-foreground mt-1">Beyond Decor. Simply Elevated.</p>
      </div>
    </div>
  );
}
